import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { getPassportAuthClient, getSupabase, isSupabaseReady } from '@/lib/supabase';
import { beginPassportLogin, isPassportEnabled, passportPublicUrl } from './passport';
import { useMeStore } from '@/store/meStore';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  sessionLoading: boolean;
  backendReady: boolean;
  busy: boolean;
  passportReady: boolean;
  signUp: () => Promise<{ ok: boolean; error?: string }>;
  logIn: (returnTo?: string) => Promise<{ ok: boolean; error?: string }>;
  logOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Auth session for HOTTR.me.
 * Prefer Passport SSO (same as HOTTR.tv). App data Supabase is optional until me schema is live.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const setIdentity = useMeStore((s) => s.setIdentityFromAuth);

  const backendReady = isSupabaseReady();
  const passportReady = isPassportEnabled();
  const user = session?.user ?? null;

  useEffect(() => {
    // Prefer data project session; fall back to Passport identity session.
    const data = getSupabase();
    const passport = getPassportAuthClient();
    const primary = data ?? passport;

    if (!primary) {
      setSessionLoading(false);
      return;
    }

    let mounted = true;

    primary.auth
      .getSession()
      .then(async ({ data: d }) => {
        if (!mounted) return;
        if (d.session) {
          setSession(d.session);
          setSessionLoading(false);
          return;
        }
        // Data project empty — try Passport client session
        if (data && passport) {
          const { data: p } = await passport.auth.getSession();
          if (!mounted) return;
          setSession(p.session ?? null);
        } else {
          setSession(null);
        }
        setSessionLoading(false);
      })
      .catch(() => {
        if (mounted) setSessionLoading(false);
      });

    const { data: sub } = primary.auth.onAuthStateChange((_event, next) => {
      if (mounted) setSession(next ?? null);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Mirror Passport user id into the local me store identity
  useEffect(() => {
    if (user?.id) {
      setIdentity({ mode: 'supabase', userId: user.id, email: user.email ?? null });
    }
  }, [user?.id, user?.email, setIdentity]);

  const signUp = useCallback(async () => {
    if (isPassportEnabled()) {
      const base = passportPublicUrl();
      if (base) {
        const url = new URL(`${base}/signup`);
        url.searchParams.set('next', '/account');
        window.location.assign(url.toString());
        return { ok: true as const };
      }
    }
    return { ok: false as const, error: 'Account sign-in isn’t available right now.' };
  }, []);

  const logIn = useCallback(async (returnTo = '/today') => {
    if (isPassportEnabled()) {
      try {
        await beginPassportLogin(returnTo);
        return { ok: true as const };
      } catch (e) {
        return {
          ok: false as const,
          error: e instanceof Error ? e.message : 'Account sign-in failed — try again.',
        };
      }
    }
    return { ok: false as const, error: 'Account sign-in isn’t available right now.' };
  }, []);

  const logOut = useCallback(async () => {
    const dataClient = getSupabase();
    const passport = getPassportAuthClient();
    setBusy(true);
    try {
      await Promise.all([
        dataClient?.auth.signOut().catch(() => undefined),
        passport?.auth.signOut().catch(() => undefined),
      ]);
      setSession(null);
    } finally {
      setBusy(false);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      sessionLoading,
      backendReady,
      busy,
      passportReady,
      signUp,
      logIn,
      logOut,
    }),
    [session, user, sessionLoading, backendReady, busy, passportReady, signUp, logIn, logOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}

export function useAuth() {
  const ctx = useAuthContext();
  return {
    session: ctx.session,
    user: ctx.user,
    loading: ctx.sessionLoading,
    ready: ctx.backendReady,
    busy: ctx.busy,
    backendReady: ctx.backendReady,
    passportReady: ctx.passportReady,
    signUp: ctx.signUp,
    logIn: ctx.logIn,
    logOut: ctx.logOut,
  };
}
